import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';

const servicesData = [
  {
    slug: 'web-development',
    icon: 'ri-global-line',
    title: 'Web Development',
    tagline: 'Scalable, secure & high-performance websites',
    description: 'We offer end-to-end web development services tailored to your business goals, ensuring speed, security, and scalability. From corporate websites to complex web applications, our team delivers pixel-perfect, performance-optimized solutions.',
    features: [
      { icon: 'ri-layout-line', title: 'Business & Corporate Websites', desc: 'Professional websites that represent your brand with stunning visuals and seamless user experience.' },
      { icon: 'ri-building-line', title: 'Startup & Enterprise Solutions', desc: 'Scalable platforms built to grow with your business from MVP to enterprise-grade systems.' },
      { icon: 'ri-shield-check-line', title: 'Secure Coding Standards', desc: 'We follow OWASP best practices ensuring your website is protected from vulnerabilities.' },
      { icon: 'ri-speed-line', title: 'Performance Optimization', desc: 'Lightning-fast load times with Core Web Vitals optimization and CDN integration.' },
      { icon: 'ri-search-eye-line', title: 'SEO Ready', desc: 'Built with SEO best practices from the ground up to help you rank higher on search engines.' },
      { icon: 'ri-smartphone-line', title: 'Mobile Responsive', desc: 'Fully responsive across all devices ensuring a consistent experience for every user.' },
    ],
    process: [
      { step: '01', title: 'Discovery & Planning', desc: 'We analyze your requirements, target audience, and business goals to create a solid plan.' },
      { step: '02', title: 'Design & Prototype', desc: 'Our designers create wireframes and interactive prototypes for your approval.' },
      { step: '03', title: 'Development', desc: 'Our engineers build your website using modern tech stacks with clean, maintainable code.' },
      { step: '04', title: 'Testing & Launch', desc: 'Rigorous QA testing across browsers and devices before a smooth deployment.' },
    ],
    technologies: ['React', 'Next.js', 'TypeScript', 'Node.js', 'PostgreSQL', 'AWS'],
  },
  {
    slug: 'mobile-app-development',
    icon: 'ri-smartphone-line',
    title: 'Mobile App Development',
    tagline: 'Native & cross-platform apps that users love',
    description: 'In a mobile-first world, having a smart, scalable, and user-friendly mobile app is essential for business growth. We build native and cross-platform applications that deliver outstanding performance and intuitive experiences.',
    features: [
      { icon: 'ri-android-line', title: 'Native Android Development', desc: 'High-performance Android apps built with Kotlin and Java for seamless device integration.' },
      { icon: 'ri-apple-line', title: 'iOS Development', desc: 'Elegant iOS apps developed with Swift that meet Apple\'s strict quality standards.' },
      { icon: 'ri-code-s-slash-line', title: 'Cross-Platform Apps', desc: 'React Native and Flutter apps that work flawlessly on both iOS and Android.' },
      { icon: 'ri-store-line', title: 'App Store Deployment', desc: 'Complete support for publishing to Google Play Store and Apple App Store.' },
      { icon: 'ri-lock-line', title: 'Secure & Scalable', desc: 'End-to-end encryption and scalable backend infrastructure for your mobile app.' },
      { icon: 'ri-bar-chart-line', title: 'Analytics Integration', desc: 'Built-in analytics to track user behavior and improve your app over time.' },
    ],
    process: [
      { step: '01', title: 'Strategy & Research', desc: 'Deep market research and competitive analysis to define your app\'s unique value.' },
      { step: '02', title: 'UI/UX Design', desc: 'Platform-specific designs following Material Design and Human Interface Guidelines.' },
      { step: '03', title: 'Agile Development', desc: 'Sprint-based development with regular demos and feedback cycles.' },
      { step: '04', title: 'QA & Deployment', desc: 'Comprehensive testing and smooth app store submission process.' },
    ],
    technologies: ['React Native', 'Flutter', 'Kotlin', 'Swift', 'Firebase', 'GraphQL'],
  },
  {
    slug: 'digital-marketing',
    icon: 'ri-megaphone-line',
    title: 'Digital Marketing',
    tagline: 'Data-driven strategies that grow your brand',
    description: "In today's digital-first world, your business needs more than just an online presence—it needs visibility, engagement, and conversions. Our data-driven digital marketing strategies deliver measurable results and sustainable growth.",
    features: [
      { icon: 'ri-search-line', title: 'Search Engine Optimization', desc: 'On-page, off-page, and technical SEO strategies to rank higher and drive organic traffic.' },
      { icon: 'ri-advertisement-line', title: 'Paid Advertising (PPC)', desc: 'Google Ads and social media campaigns optimized for maximum ROI.' },
      { icon: 'ri-instagram-line', title: 'Social Media Marketing', desc: 'Engaging content strategies across all major social platforms to grow your community.' },
      { icon: 'ri-mail-send-line', title: 'Email Marketing', desc: 'Targeted email campaigns with personalization and automation for higher conversions.' },
      { icon: 'ri-article-line', title: 'Content Marketing', desc: 'High-quality, SEO-optimized content that establishes your brand as an industry authority.' },
      { icon: 'ri-line-chart-line', title: 'Analytics & Reporting', desc: 'Detailed monthly reports with actionable insights to continuously improve performance.' },
    ],
    process: [
      { step: '01', title: 'Audit & Analysis', desc: 'Complete audit of your current digital presence and competitor landscape.' },
      { step: '02', title: 'Strategy Development', desc: 'Custom multi-channel marketing strategy aligned with your business goals.' },
      { step: '03', title: 'Execution & Management', desc: 'Full campaign management with continuous optimization and A/B testing.' },
      { step: '04', title: 'Measure & Optimize', desc: 'Regular reporting with data-backed improvements to maximize performance.' },
    ],
    technologies: ['Google Analytics', 'SEMrush', 'HubSpot', 'Mailchimp', 'Meta Ads', 'Google Ads'],
  },
  {
    slug: 'ui-ux-design',
    icon: 'ri-palette-line',
    title: 'UI/UX Design',
    tagline: 'Beautiful designs that delight and convert',
    description: 'Create stunning, user-centric designs that delight your users and drive engagement across all platforms. Our design team combines aesthetic excellence with deep UX research to create interfaces that are both beautiful and intuitive.',
    features: [
      { icon: 'ri-user-search-line', title: 'User Research & Testing', desc: 'In-depth user interviews, usability testing, and persona development to guide design decisions.' },
      { icon: 'ri-layout-grid-line', title: 'Wireframing & Prototyping', desc: 'Interactive prototypes to validate concepts before development begins.' },
      { icon: 'ri-brush-line', title: 'Visual Design Systems', desc: 'Comprehensive design systems with reusable components for consistent brand experiences.' },
      { icon: 'ri-cursor-line', title: 'Interaction Design', desc: 'Micro-animations and transitions that make your interface feel alive and responsive.' },
      { icon: 'ri-eye-line', title: 'Accessibility (WCAG)', desc: 'Inclusive designs that meet WCAG 2.1 standards for all users.' },
      { icon: 'ri-contrast-line', title: 'Brand Identity', desc: 'Cohesive visual identity from logos to full brand guidelines and style guides.' },
    ],
    process: [
      { step: '01', title: 'Research & Discovery', desc: 'User interviews, competitor analysis, and stakeholder workshops to define the vision.' },
      { step: '02', title: 'Information Architecture', desc: 'Sitemap and user flow creation to ensure intuitive navigation.' },
      { step: '03', title: 'Design & Iterate', desc: 'High-fidelity designs with multiple iterations based on feedback.' },
      { step: '04', title: 'Handoff & Support', desc: 'Developer-ready design specs and ongoing support during implementation.' },
    ],
    technologies: ['Figma', 'Adobe XD', 'Sketch', 'InVision', 'Zeplin', 'Principle'],
  },
  {
    slug: 'cloud-solutions',
    icon: 'ri-cloud-line',
    title: 'Cloud Solutions',
    tagline: 'Scalable cloud infrastructure for modern businesses',
    description: 'Leverage the power of cloud computing to scale your business operations and reduce infrastructure costs. From cloud migration to fully managed cloud environments, we help you harness the full potential of AWS, Azure, and Google Cloud.',
    features: [
      { icon: 'ri-upload-cloud-line', title: 'Cloud Migration', desc: 'Seamless migration of your existing infrastructure to the cloud with zero downtime.' },
      { icon: 'ri-server-line', title: 'AWS / Azure / GCP', desc: 'Certified experts across all major cloud platforms to optimize your cloud spend.' },
      { icon: 'ri-function-line', title: 'Serverless Architecture', desc: 'Cost-efficient serverless solutions that scale automatically with your demand.' },
      { icon: 'ri-git-branch-line', title: 'DevOps & CI/CD', desc: 'Automated pipelines for faster, more reliable software delivery.' },
      { icon: 'ri-shield-star-line', title: 'Cloud Security', desc: 'IAM, encryption, and compliance management to keep your cloud environment secure.' },
      { icon: 'ri-settings-3-line', title: 'Infrastructure as Code', desc: 'Terraform and CloudFormation for reproducible, version-controlled infrastructure.' },
    ],
    process: [
      { step: '01', title: 'Cloud Assessment', desc: 'Evaluate your current infrastructure and define the optimal cloud architecture.' },
      { step: '02', title: 'Migration Planning', desc: 'Detailed migration roadmap with risk mitigation strategies.' },
      { step: '03', title: 'Implementation', desc: 'Phased migration and cloud setup with continuous monitoring.' },
      { step: '04', title: 'Optimize & Manage', desc: 'Ongoing cost optimization, security audits, and performance tuning.' },
    ],
    technologies: ['AWS', 'Azure', 'Google Cloud', 'Terraform', 'Kubernetes', 'Docker'],
  },
  {
    slug: 'it-consulting',
    icon: 'ri-customer-service-line',
    title: 'IT Consulting',
    tagline: 'Strategic technology guidance for your business',
    description: 'Strategic technology consulting to help you make informed decisions and achieve your business objectives. Our consultants bring decades of industry experience to guide your digital transformation journey.',
    features: [
      { icon: 'ri-road-map-line', title: 'Technology Roadmap', desc: 'Clear, actionable technology roadmaps aligned with your 3-5 year business vision.' },
      { icon: 'ri-refresh-line', title: 'Digital Transformation', desc: 'End-to-end transformation strategies to modernize your business processes.' },
      { icon: 'ri-flow-chart', title: 'Process Optimization', desc: 'Identify inefficiencies and implement technology solutions to streamline operations.' },
      { icon: 'ri-alert-line', title: 'Risk Assessment', desc: 'Comprehensive IT risk assessments and mitigation strategies.' },
      { icon: 'ri-team-line', title: 'Team Augmentation', desc: 'Skilled IT professionals to supplement your existing team on demand.' },
      { icon: 'ri-award-line', title: 'Compliance & Governance', desc: 'Ensure your IT infrastructure meets industry standards and regulatory requirements.' },
    ],
    process: [
      { step: '01', title: 'Initial Assessment', desc: 'Deep-dive analysis of your current IT landscape, challenges, and goals.' },
      { step: '02', title: 'Strategy Workshop', desc: 'Collaborative workshops to align stakeholders and define the transformation vision.' },
      { step: '03', title: 'Recommendations', desc: 'Detailed recommendations report with prioritized action items and ROI projections.' },
      { step: '04', title: 'Implementation Support', desc: 'Hands-on support throughout implementation to ensure successful outcomes.' },
    ],
    technologies: ['ITIL', 'TOGAF', 'Agile', 'Scrum', 'SAFe', 'COBIT'],
  },
];

const ServicePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const service = servicesData.find(s => s.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <SeoHead
          title="Service Not Found | BitNinja Technologies"
          description="The requested IT service page could not be found. Explore our full range of web development, mobile apps, cloud solutions, and digital marketing services."
          canonicalUrl="https://bitninjatech.com/services"
          jsonLd={{ "@context": "https://schema.org", "@type": "WebPage", "name": "Service Not Found", "url": "https://bitninjatech.com/services" }}
        />
        <div className="text-center">
          <h1 className="text-4xl font-bold text-foreground-900 mb-4">Service Not Found</h1>
          <Link to="/" className="text-primary-500 hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const serviceJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": `${service.title} — BitNinja Technologies`,
      "provider": {
        "@type": "Organization",
        "name": "BitNinja Technologies",
        "url": "https://bitninjatech.com"
      },
      "description": service.description,
      "serviceType": service.title,
      "areaServed": ["IN", "US", "Worldwide"],
      "url": `https://bitninjatech.com/services/${service.slug}`,
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "areaServed": "Worldwide"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://bitninjatech.com" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://bitninjatech.com/#services" },
        { "@type": "ListItem", "position": 3, "name": service.title, "item": `https://bitninjatech.com/services/${service.slug}` }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": `What ${service.title.toLowerCase()} services does BitNinja offer?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": service.description
          }
        },
        {
          "@type": "Question",
          "name": `What technologies does BitNinja use for ${service.title.toLowerCase()}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `We use ${service.technologies.join(', ')} for ${service.title.toLowerCase()} projects, ensuring modern, scalable, and secure solutions.`
          }
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHead
        title={`${service.title} | BitNinja Technologies`}
        description={service.description}
        canonicalUrl={`https://bitninjatech.com/services/${service.slug}`}
        jsonLd={serviceJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 md:pt-32 overflow-hidden bg-foreground-900 min-h-[480px] flex items-center">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, oklch(var(--background-50)) 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}></div>
        </div>
        <div className="section-padding relative z-10 w-full py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-background-400 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <button onClick={() => window.location.href = '/#services'} className="hover:text-primary-500 transition-colors cursor-pointer">Services</button>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-primary-500">{service.title}</span>
            </div>
            <div className="w-16 h-16 rounded-xl bg-primary-500 flex items-center justify-center mb-6">
              <i className={`${service.icon} text-3xl text-background-50`}></i>
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4">{service.title}</h1>
            <p className="text-background-300 text-xl mb-8 max-w-2xl">{service.tagline}</p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact-cta" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-colors whitespace-nowrap">
                Get a Free Quote
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
              <a href="#features" className="inline-flex items-center gap-2 bg-background-50/10 backdrop-blur-sm hover:bg-background-50/20 text-background-50 px-6 py-3 rounded-full text-sm font-semibold border border-background-50/20 transition-colors whitespace-nowrap">
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-14 md:py-16 bg-background-50 border-t border-background-200">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex w-6 h-6 items-center justify-center bg-foreground-900 text-background-50 rounded-sm">
                    <i className="ri-file-list-3-line text-xs"></i>
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-600">
                    Overview
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-bold leading-[1.02] tracking-tight text-foreground-900 max-w-3xl">
                  What We{' '}
                  <span className="block text-primary-500">Offer.</span>
                </h2>
              </div>
              <p className="max-w-md text-[15px] leading-relaxed text-foreground-700">
                {service.description}
              </p>
            </div>

            <div className="bg-background-100 rounded-2xl p-7 md:p-9 border border-background-200">
              <h3 className="font-display font-bold text-foreground-900 text-lg mb-4">Technologies We Use</h3>
              <div className="flex flex-wrap gap-2">
                {service.technologies.map((tech, i) => (
                  <span key={i} className="bg-background-50 text-foreground-900 text-sm font-medium px-4 py-2 rounded-full border border-background-200 font-mono text-[10px] uppercase tracking-widest">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-14 md:py-16 bg-background-100 border-t border-background-200">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex w-6 h-6 items-center justify-center bg-foreground-900 text-background-50 rounded-sm">
                    <i className="ri-stack-line text-xs"></i>
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-600">
                    Features
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-bold leading-[1.02] tracking-tight text-foreground-900 max-w-3xl">
                  Key{' '}
                  <span className="block text-primary-500">Capabilities.</span>
                </h2>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.features.map((feature, i) => (
                <div key={i} className="bg-background-50 rounded-2xl p-6 border border-background-200 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-foreground-900 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <i className={`${feature.icon} text-xl text-background-50`}></i>
                  </div>
                  <h3 className="font-display font-bold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-14 md:py-16 bg-foreground-900 border-t border-foreground-800">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="inline-flex w-6 h-6 items-center justify-center bg-primary-500 text-background-50 rounded-sm">
                  <i className="ri-route-line text-xs"></i>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background-400">
                  Our Approach
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-background-50">
                How We <span className="text-primary-500">Work.</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {service.process.map((step, i) => (
                <div key={i} className="relative">
                  {i < service.process.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-primary-500/30 z-0"></div>
                  )}
                  <div className="relative z-10 bg-background-50/5 border border-background-50/10 rounded-2xl p-6 hover:border-primary-500/50 transition-all">
                    <div className="text-4xl font-bold text-primary-500/30 font-display mb-4">{step.step}</div>
                    <h3 className="font-display font-bold text-background-50 mb-2">{step.title}</h3>
                    <p className="text-background-400 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-14 md:py-16 bg-background-50 border-t border-background-200">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-display text-3xl font-bold text-foreground-900">
                Explore Other <span className="text-primary-500">Services.</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {servicesData.filter(s => s.slug !== slug).map((s, i) => (
                <Link
                  key={i}
                  to={`/services/${s.slug}`}
                  className="group flex items-center gap-4 p-5 rounded-xl border border-background-200 hover:border-foreground-900/30 hover:bg-background-100 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-foreground-900 flex items-center justify-center flex-shrink-0">
                    <i className={`${s.icon} text-xl text-background-50`}></i>
                  </div>
                  <div>
                    <div className="font-display font-bold text-foreground-900 text-sm group-hover:text-primary-500 transition-colors">{s.title}</div>
                    <div className="text-foreground-500 text-xs mt-0.5">{s.tagline}</div>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 ml-auto transition-colors"></i>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact-cta" className="py-14 md:py-16 bg-primary-500">
        <div className="section-padding">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-background-50 mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-background-50/80 text-lg mb-8 max-w-2xl mx-auto">
              Let&apos;s talk about your project and how our {service.title} expertise can help your business grow.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/#contact" className="inline-flex items-center gap-2 bg-background-50 text-primary-500 hover:bg-background-100 px-6 py-3 rounded-full text-sm font-semibold transition-colors whitespace-nowrap">
                Contact Us Today
                <i className="ri-arrow-right-up-line text-base"></i>
              </Link>
              <Link to="/#pricing" className="inline-flex items-center gap-2 border border-background-50/40 hover:border-background-50 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-colors whitespace-nowrap">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePage;